import { normalizeForSearch } from "./searchText";

export const PROFILE_HIGHLIGHT_QUERY_PARAM = "highlight";

export const PROFILE_SEARCH_HIGHLIGHT_CLASS = "profile-search-highlight";

const SKIP_ANCESTOR_SELECTOR =
  "script, style, noscript, textarea, input, select, option, button, mark, svg, [data-skip-text-highlight]";

export type DomTextHighlightOptions = {
  className?: string;
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function shouldSkipTextNode(node: Text): boolean {
  const parent = node.parentElement;
  if (!parent) {
    return true;
  }
  if (parent.closest(SKIP_ANCESTOR_SELECTOR)) {
    return true;
  }
  if (parent.isContentEditable) {
    return true;
  }
  return false;
}

function collectTextNodes(root: HTMLElement): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    const textNode = current as Text;
    if (!shouldSkipTextNode(textNode)) {
      const value = textNode.nodeValue ?? "";
      if (value.trim()) {
        nodes.push(textNode);
      }
    }
    current = walker.nextNode();
  }
  return nodes;
}

/** Case- and accent-insensitive substring matches mapped to original string indices. */
export function findHighlightRanges(
  text: string,
  query: string,
): Array<{ start: number; end: number }> {
  const normalizedQuery = normalizeForSearch(query);
  if (!normalizedQuery) {
    return [];
  }

  const normalizedText = normalizeForSearch(text);
  const ranges: Array<{ start: number; end: number }> = [];
  let from = 0;

  while (from < normalizedText.length) {
    const index = normalizedText.indexOf(normalizedQuery, from);
    if (index === -1) {
      break;
    }
    ranges.push({ start: index, end: index + normalizedQuery.length });
    from = index + normalizedQuery.length || index + 1;
  }

  return ranges;
}

function wrapTextNodeMatches(
  textNode: Text,
  ranges: Array<{ start: number; end: number }>,
  className: string,
): void {
  const text = textNode.nodeValue ?? "";
  if (!ranges.length) {
    return;
  }

  const fragment = document.createDocumentFragment();
  let lastIndex = 0;

  for (const { start, end } of ranges) {
    if (start > lastIndex) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
    }
    const mark = document.createElement("mark");
    mark.className = className;
    mark.textContent = text.slice(start, end);
    fragment.appendChild(mark);
    lastIndex = end;
  }

  if (lastIndex < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
  }

  textNode.parentNode?.replaceChild(fragment, textNode);
}

export function clearDomTextHighlight(
  root: HTMLElement,
  className: string = PROFILE_SEARCH_HIGHLIGHT_CLASS,
): void {
  root.querySelectorAll(`mark.${className}`).forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) {
      return;
    }
    parent.replaceChild(
      document.createTextNode(mark.textContent ?? ""),
      mark,
    );
    parent.normalize();
  });
}

export function applyDomTextHighlight(
  root: HTMLElement,
  query: string,
  options: DomTextHighlightOptions = {},
): void {
  const term = String(query ?? "").trim();
  const className = options.className ?? PROFILE_SEARCH_HIGHLIGHT_CLASS;
  if (!term) {
    return;
  }

  clearDomTextHighlight(root, className);

  const regex = new RegExp(escapeRegExp(term), "gi");

  for (const textNode of collectTextNodes(root)) {
    const text = textNode.nodeValue ?? "";
    const ranges = findHighlightRanges(text, term);
    if (ranges.length > 0) {
      wrapTextNodeMatches(textNode, ranges, className);
      continue;
    }

    // Fallback for edge cases where normalization diverges from visible text.
    regex.lastIndex = 0;
    if (!regex.test(text)) {
      continue;
    }
    regex.lastIndex = 0;

    const fallbackRanges: Array<{ start: number; end: number }> = [];
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      fallbackRanges.push({
        start: match.index,
        end: match.index + match[0].length,
      });
      if (match[0].length === 0) {
        regex.lastIndex += 1;
      }
    }
    wrapTextNodeMatches(textNode, fallbackRanges, className);
  }
}
